/*
  Edit Customization: reopens a customized bat from the cart with every
  choice filled back in, and swaps "Add to Arsenal" for "Update Bat".

  The cart's Edit Customization link opens the product at
  ?variant=<id>&edit_line=<cart line key>. Nothing is stored in the browser:
  the cart line itself holds every choice as line-item properties, so this
  reads the line from /cart.js and restores the page from it.

  Saving removes the old cart line, then lets the theme's normal add-to-cart
  run. If that add fails, the old line is put back, so an edit can never
  lose the bat. On success the shopper goes back to the cart.
*/
(() => {
  const lineKey = new URLSearchParams(window.location.search).get('edit_line');
  if (!lineKey) return;

  const UPDATE_LABEL = 'Update Bat';
  const cartUrl = () => window.Theme?.routes?.cart_url || '/cart';

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`${url} ${response.status}`);
      return response.json();
    });

  const dropEditParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('edit_line');
    window.history.replaceState(null, '', url);
  };

  const whenReady = () =>
    new Promise((resolve) => {
      if (document.readyState === 'complete') resolve();
      else window.addEventListener('load', resolve, { once: true });
    }).then(() =>
      Promise.all(
        ['color-property-picker', 'logo-color-picker', 'product-custom-property-component'].map((tag) =>
          document.querySelector(tag) ? customElements.whenDefined(tag) : null
        )
      )
    );

  // --- Restoring the saved choices -------------------------------------

  function restore(line) {
    Object.entries(line.properties || {}).forEach(([key, value]) => {
      if (key.startsWith('_') || value == null || value === '') return;

      const swatch =
        document.querySelector(
          `color-property-picker[data-property-key="${CSS.escape(key)}"] [data-color-property-swatch="${CSS.escape(value)}"]`
        ) ||
        (key === 'Logo Color' &&
          document.querySelector(`logo-color-picker [data-logo-color-swatch="${CSS.escape(value)}"]`));
      if (swatch) {
        swatch.click();
        return;
      }

      // Pill options saved as properties (e.g. Weight): check the matching one.
      const radio = document.querySelector(
        `.product-details input[type="radio"][name="properties[${CSS.escape(key)}]"][value="${CSS.escape(value)}"]`
      );
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const field = document.querySelector(
        `.product-details input[name="properties[${CSS.escape(key)}]"]:not([type="hidden"]), .product-details textarea[name="properties[${CSS.escape(key)}]"]`
      );
      if (!field) return;

      // Optional fields like Top Line start collapsed behind a "+ Add" toggle.
      // Open it directly; clicking the toggle would also focus (and scroll to)
      // the field.
      const component = field.closest('product-custom-property-component');
      const toggle = component?.querySelector('.__toggle');
      const content = component?.querySelector('.__content');
      if (toggle && !toggle.hidden) {
        toggle.hidden = true;
        toggle.setAttribute('aria-expanded', 'true');
        if (content) content.hidden = false;
      }

      field.value = value;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const quantity = document.querySelector('.product-details .buy-buttons-block input[name="quantity"]');
    if (quantity && line.quantity) {
      quantity.value = line.quantity;
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // --- "Update Bat" labels + notice -------------------------------------

  function mainButton() {
    return document.querySelector('.product-details .buy-buttons-block button[name="add"]');
  }

  function relabelMainButton() {
    const button = mainButton();
    if (!button) return;
    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()
    );
    if (textNode && textNode.nodeValue.trim() !== UPDATE_LABEL) textNode.nodeValue = UPDATE_LABEL;
  }

  function relabelStickyBar() {
    const bar = document.querySelector('sticky-buy-bar');
    const label = bar?.querySelector('[data-sticky-buy-label]');
    if (!bar || !label) return;
    bar.defaultLabel = UPDATE_LABEL;
    if (!bar.dataset.state || bar.dataset.state === 'idle') label.textContent = UPDATE_LABEL;
  }

  function showNotice() {
    const block = document.querySelector('.product-details .buy-buttons-block');
    if (!block || document.querySelector('.cart-line-edit-notice')) return;
    const notice = document.createElement('p');
    notice.className = 'cart-line-edit-notice';
    notice.textContent = 'Editing a bat in your cart. Update Bat replaces it with these choices.';
    block.before(notice);
  }

  function enterEditMode() {
    relabelMainButton();
    relabelStickyBar();
    showNotice();

    // Variant changes re-render the buy buttons; keep the label.
    const block = document.querySelector('.product-details .buy-buttons-block');
    if (block) new MutationObserver(relabelMainButton).observe(block, { childList: true, subtree: true, characterData: true });
    // The sticky bar may connect after this runs.
    customElements.whenDefined('sticky-buy-bar').then(() => setTimeout(relabelStickyBar, 0));
  }

  // --- Saving -------------------------------------------------------------

  function interceptSubmit(line) {
    let removed = false;
    let saving = false;

    // Window capture runs before the theme's own (document-level) submit
    // handling, so the old line is always removed before anything is added.
    window.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.closest('.product-details .buy-buttons-block')) return;
        if (removed) return; // second pass: let the theme's add-to-cart run

        event.preventDefault();
        event.stopImmediatePropagation();
        if (saving) return;
        saving = true;

        try {
          await postJSON('/cart/change.js', { id: line.key, quantity: 0 });
        } catch (error) {
          // The line is already gone (e.g. edited in another tab): just add.
          console.warn('cart-line-edit: old line not removed', error);
        }
        removed = true;

        const onUpdate = (updateEvent) => {
          document.removeEventListener('shopify:cart:lines-update', onUpdate);
          const promise = updateEvent.promise || updateEvent.detail?.promise;
          Promise.resolve(promise)
            .then((result) => {
              if (result?.detail?.didError) throw new Error('add failed');
              dropEditParam();
              window.location.href = cartUrl();
            })
            .catch(async () => {
              // Put the original bat back so nothing is lost.
              await postJSON('/cart/add.js', {
                items: [{ id: line.variant_id, quantity: line.quantity, properties: line.properties }],
              }).catch(() => {});
              removed = false;
              saving = false;
            });
        };
        document.addEventListener('shopify:cart:lines-update', onUpdate);

        form.requestSubmit(mainButton() || undefined);
      },
      true
    );
  }

  Promise.all([fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()), whenReady()])
    .then(([cart]) => {
      const line = (cart.items || []).find((item) => item.key === lineKey);
      if (!line) {
        // Stale link (cart changed): fall back to a normal product page.
        dropEditParam();
        return;
      }
      restore(line);
      enterEditMode();
      interceptSubmit(line);
    })
    .catch((error) => console.error('cart-line-edit: could not load cart line', error));
})();
